import {
  ICommentRequestParamDto,
  IPaginationResponseDto,
  IComment,
  CommentId,
  ICommentReport,
  CommentReviewId,
  CommentReviewStatus,
  IApiResult,
  ICommentReview,
  IProfileImageRequestParamDto,
  IProfileImage,
  UserId,
  ProfileImageReviewId,
  ProfileImageReviewStatus,
  IProfileImageReview,
  IDisplayNameRequestParamDto,
  IDisplayName,
  DisplayNameReviewId,
  DisplayNameReviewStatus,
  IDisplayNameReview,
  IBlockedWord,
  IBlockedDisplayName,
  IViolationHistory,
  IBannedUser,
} from '../types';
import { AxiosResponse, Canceler } from 'axios';
import settings from '../settings';
import api from './base';

export function getBannedUsers(
  payload: ICommentRequestParamDto,
  page = 1,
  limit = 20,
): [Promise<AxiosResponse<IPaginationResponseDto<IBannedUser>>>, Canceler] {
  const uri = `${settings.getApiURI()}/user-admin/v1/users/banned`;

  return api.getWithCancel(uri, {
    params: {
      page,
      limit,
      ...payload,
    },
  });
}

export function changeUserBanStatus(userId: number, status: 'ban' | 'unban') {
  const uri = `${settings.getApiURI()}/user-admin/v1/users/${userId}/ban-status`;

  return api.http.put(uri, { status });
}

export function saveViolationHistoryReason(violationHistoryId: number, reason: string): Promise<AxiosResponse<IApiResult<IViolationHistory>>> {
  const uri = `${settings.getApiURI()}/user-admin/v1/violation-histories/${violationHistoryId}`;

  return api.http.patch(uri, { reason });
}

export function getComments(
  payload: ICommentRequestParamDto,
  page = 1,
  limit = 20,
): [Promise<AxiosResponse<IPaginationResponseDto<IComment>>>, Canceler] {
  const uri = `${settings.getApiURI()}/comment/v1/comments-for-admin`;

  return api.getWithCancel(uri, {
    params: {
      page,
      limit,
      ...payload,
    },
  });
}

export function getCommentReports(commentId: CommentId, page = 1, limit = 20): Promise<AxiosResponse<IPaginationResponseDto<ICommentReport>>> {
  const uri = `${settings.getApiURI()}/comment/v1/comments/${commentId}/reports`;

  return api.http.get(uri, {
    params: {
      page,
      limit,
    },
  });
}

export function saveCommentReviewStatus(
  commentReviewIds: CommentReviewId[],
  status: CommentReviewStatus,
): Promise<AxiosResponse<IApiResult<ICommentReview[]>>> {
  const uri = `${settings.getApiURI()}/comment/v1/comment-reviews`;

  return api.http.patch(uri, {
    ids: commentReviewIds,
    status,
  });
}

export function saveCommentReviewInternalNote(commentReviewId: CommentReviewId, note: string): Promise<AxiosResponse<IApiResult<ICommentReview>>> {
  const uri = `${settings.getApiURI()}/comment/v1/comment-reviews/${commentReviewId}`;

  return api.http.patch(uri, { note });
}

export function getProfileImages(
  payload: IProfileImageRequestParamDto,
  page = 1,
  limit = 20,
): Promise<AxiosResponse<IPaginationResponseDto<IProfileImage>>> {
  const uri = `${settings.getApiURI()}/user/v1/photos`;

  return api.http.get(uri, {
    params: {
      page,
      limit,
      ...payload,
    },
  });
}

export function getProfileImageUpdatedHistories(userId: UserId, page = 1, limit = 20): Promise<AxiosResponse<IPaginationResponseDto<IProfileImage>>> {
  const uri = `${settings.getApiURI()}/user/v1/users/${userId}/photos`;

  return api.http.get(uri, {
    params: {
      page,
      limit,
    },
  });
}

export function saveProfileImageReviewStatus(
  profileImageReviewIds: ProfileImageReviewId[],
  status: ProfileImageReviewStatus,
): Promise<AxiosResponse<IApiResult<IProfileImageReview[]>>> {
  const uri = `${settings.getApiURI()}/user/v1/photo-reviews`;

  return api.http.patch(uri, {
    ids: profileImageReviewIds,
    status,
  });
}

export function getDisplayNames(
  payload: IDisplayNameRequestParamDto,
  page = 1,
  limit = 20,
): Promise<AxiosResponse<IPaginationResponseDto<IDisplayName>>> {
  const uri = `${settings.getApiURI()}/user/v1/display-names`;

  return api.http.get(uri, {
    params: {
      page,
      limit,
      ...payload,
    },
  });
}

export function getDisplayNameChangeHistories(userId: UserId, page = 1, limit = 20): Promise<AxiosResponse<IPaginationResponseDto<IDisplayName>>> {
  const uri = `${settings.getApiURI()}/user/v1/users/${userId}/display-names`;

  return api.http.get(uri, {
    params: {
      page,
      limit,
    },
  });
}

export function saveDisplayNameReviewStatus(
  displayNameReviewIds: DisplayNameReviewId[],
  status: DisplayNameReviewStatus,
): Promise<AxiosResponse<IApiResult<IDisplayNameReview[]>>> {
  const uri = `${settings.getApiURI()}/user/v1/display-name-reviews`;

  return api.http.patch(uri, {
    ids: displayNameReviewIds,
    status,
  });
}

export function saveDisplayNameReviewInternalNote(
  displayNameReviewId: DisplayNameReviewId,
  note: string,
): Promise<AxiosResponse<IApiResult<IDisplayNameReview>>> {
  const uri = `${settings.getApiURI()}/user/v1/display-name-reviews/${displayNameReviewId}`;

  return api.http.patch(uri, { note });
}

export function getBlockedWords(): Promise<AxiosResponse<IBlockedWord[]>> {
  const uri = `${settings.getApiURI()}/comment/v1/blocked-words`;

  return api.http.get(uri);
}

export function addBlockedWord(contents: string): Promise<AxiosResponse<IApiResult<IBlockedWord>>> {
  const uri = `${settings.getApiURI()}/comment/v1/blocked-words`;

  return api.http.post(uri, {
    contents,
  });
}

export function deleteBlockedWord(id: number) {
  const uri = `${settings.getApiURI()}/comment/v1/blocked-words/${id}`;

  return api.http.delete(uri);
}

export function getBlockedDisplayNames(): Promise<AxiosResponse<IBlockedDisplayName[]>> {
  const uri = `${settings.getApiURI()}/user/v1/blocked-display-names`;

  return api.http.get(uri);
}

export function addBlockedDisplayName(contents: string): Promise<AxiosResponse<IApiResult<IBlockedDisplayName>>> {
  const uri = `${settings.getApiURI()}/user/v1/blocked-display-names`;

  return api.http.post(uri, {
    contents,
  });
}

export function deleteBlockedDisplayName(id: number) {
  const uri = `${settings.getApiURI()}/user/v1/blocked-display-names/${id}`;

  return api.http.delete(uri);
}

export function banCommentAndUser(commentReviewId: CommentReviewId): Promise<AxiosResponse<IApiResult<ICommentReview>>> {
  const uri = `${settings.getApiURI()}/comment/v1/comment-reviews/${commentReviewId}/commands/ban`;

  return api.http.patch(uri);
}

export function unbanCommentAndUser(commentReviewId: CommentReviewId): Promise<AxiosResponse<IApiResult<ICommentReview>>> {
  const uri = `${settings.getApiURI()}/comment/v1/comment-reviews/${commentReviewId}/commands/unban`;

  return api.http.patch(uri);
}
